/**
 * Canonical project-first sidebar: one row for each environment-scoped project,
 * with that project's threads nested underneath. Environment identity belongs
 * to the project row, not to every thread row.
 */

import { memo } from "react";
import type { ReactNode } from "react";
import {
  DndContext,
  type CollisionDetection,
  type DragCancelEvent,
  type DragEndEvent,
  type DragStartEvent,
  type useSensors as UseSensorsType,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ChevronsUpDownIcon, FolderPlusIcon, UnfoldVerticalIcon } from "lucide-react";
import type { ProjectImportJob } from "~/projectImportStore";
import { SidebarFixedHeader, SidebarGroup, SidebarMenu, SidebarMenuItem } from "../ui/sidebar";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { DENSE_RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";
import { ProjectEnvironmentIcon } from "./ProjectEnvironmentIcon";
import { SidebarProjectItem } from "./SidebarProjectItem";
import type { SidebarProjectItemProps } from "./SidebarProjectItem";
import { ProjectImportJobRow, SortableProjectRow } from "./LocalProjectRows";
import { ProjectRuntimeControls, type ProjectRuntimeControl } from "./ProjectRuntimeControls";
import {
  ProjectGroupedSidebarEmptyState,
  type ProjectGroupedSidebarCloudStatusModel,
  type ProjectGroupedSidebarEmptyStateModel,
} from "./ProjectGroupedSidebarEmptyState";
import { resolveCanonicalProjectIdentity } from "./projectGroupedSidebar.logic";
import type { ProjectGroup } from "./projectGroupedSidebar.logic";
import type { SidebarThreadSummary } from "~/types";
import type {
  SidebarProjectSortOrder,
  SidebarThreadSortOrder,
} from "@cmmd-forge/contracts/settings";
import type { SidebarDensity } from "~/uiStateStore";
import { ProjectSidebarViewOptionsMenu } from "./ProjectSidebarViewOptionsMenu";
import { useSidebarViewFiltersStore } from "~/sidebarViewFiltersStore";
import {
  applySidebarViewFilters,
  buildSidebarGroupSections,
  isManualOrderingAvailable,
  type SidebarGroupSection,
} from "./sidebarViewFilters";

interface ProjectGroupedSidebarProps {
  readonly navigationChrome: ReactNode;
  readonly groups: readonly ProjectGroup[];
  readonly projectExpandedById: Readonly<Record<string, boolean>>;
  readonly setProjectExpanded: (projectKey: string, expanded: boolean) => void;
  readonly handleStartAddProject: () => void;
  readonly density: SidebarDensity;
  readonly projectSortOrder: SidebarProjectSortOrder;
  readonly threadSortOrder: SidebarThreadSortOrder;
  readonly onDensityChange: (density: SidebarDensity) => void;
  readonly onProjectSortOrderChange: (order: SidebarProjectSortOrder) => void;
  readonly onThreadSortOrderChange: (order: SidebarThreadSortOrder) => void;
  readonly expandedThreadListsByProject: ReadonlySet<string>;
  readonly isManualProjectSorting: boolean;
  readonly attachProjectListAutoAnimateRef: (element: HTMLElement | null) => void;
  readonly projectDnDSensors: ReturnType<typeof UseSensorsType>;
  readonly projectCollisionDetection: CollisionDetection;
  readonly onProjectDragStart: (event: DragStartEvent) => void;
  readonly onProjectDragEnd: (event: DragEndEvent) => void;
  readonly onProjectDragCancel: (event: DragCancelEvent) => void;
  readonly projectImportJobs: readonly ProjectImportJob[];
  readonly dismissProjectImportJob: (jobId: string) => void;
  readonly runtimeControls: readonly ProjectRuntimeControl[];
  readonly cloudStatus: ProjectGroupedSidebarCloudStatusModel | null;
  readonly emptyState: ProjectGroupedSidebarEmptyStateModel;
  readonly projectItemProps: Omit<
    SidebarProjectItemProps,
    | "project"
    | "isThreadListExpanded"
    | "isManualProjectSorting"
    | "dragHandleProps"
    | "environmentIndicator"
    | "rootDataAttributes"
  >;
  readonly looseThreads: readonly SidebarThreadSummary[];
  readonly renderLooseThreadList: (
    workspaceId: string,
    threads: readonly SidebarThreadSummary[],
  ) => ReactNode;
}

interface ProjectsHeaderProps {
  readonly allCollapsed: boolean;
  readonly hasProjects: boolean;
  readonly onAddProject: () => void;
  readonly onToggleAllCollapsed: () => void;
  readonly viewOptions: ReactNode;
}

function ProjectsHeader({
  allCollapsed,
  hasProjects,
  onAddProject,
  onToggleAllCollapsed,
  viewOptions,
}: ProjectsHeaderProps) {
  const toggleLabel = allCollapsed ? "Expand all projects" : "Collapse all projects";
  return (
    <div className="mb-1 flex min-h-touch items-center px-2 md:h-6 md:min-h-0">
      <div className="flex min-w-0 flex-1 items-center">
        <span className="truncate text-[11px] font-medium tracking-[0.08em] text-muted-foreground/50 uppercase">
          Projects
        </span>
      </div>
      <div className="inline-flex items-center gap-2 md:-mr-0.5 md:gap-0.5">
        {viewOptions}
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label={toggleLabel}
                disabled={!hasProjects}
                className={`${DENSE_RESPONSIVE_ICON_BUTTON_CLASS} inline-flex cursor-pointer items-center justify-center text-muted-foreground/70 transition-colors hover:bg-accent/65 hover:text-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-default disabled:opacity-35`}
                onClick={onToggleAllCollapsed}
              />
            }
          >
            {allCollapsed ? (
              <UnfoldVerticalIcon className="size-3.5" />
            ) : (
              <ChevronsUpDownIcon className="size-3.5" />
            )}
          </TooltipTrigger>
          <TooltipPopup side="top">{toggleLabel}</TooltipPopup>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label="Add project"
                className={`${DENSE_RESPONSIVE_ICON_BUTTON_CLASS} inline-flex cursor-pointer items-center justify-center text-muted-foreground/70 transition-colors hover:bg-accent/65 hover:text-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring`}
                onClick={onAddProject}
              />
            }
          >
            <FolderPlusIcon className="size-3.5" />
          </TooltipTrigger>
          <TooltipPopup side="top">Add project</TooltipPopup>
        </Tooltip>
      </div>
    </div>
  );
}

function ProjectGroupRow(props: {
  readonly group: ProjectGroup;
  readonly isThreadListExpanded: boolean;
  readonly isManualProjectSorting: boolean;
  readonly projectItemProps: ProjectGroupedSidebarProps["projectItemProps"];
}) {
  const { group, isThreadListExpanded, isManualProjectSorting, projectItemProps } = props;
  const canonicalIdentity = resolveCanonicalProjectIdentity(group);
  const sharedProps = {
    ...projectItemProps,
    project: group.representative,
    isThreadListExpanded,
    environmentIndicator: (
      <ProjectEnvironmentIcon kind={group.environmentKind} label={group.environmentLabel} />
    ),
    rootDataAttributes: { "data-project-group-key": group.groupKey },
    ...(canonicalIdentity ? { projectIcon: canonicalIdentity } : {}),
  };

  if (isManualProjectSorting) {
    return <SortableProjectRow {...sharedProps} isManualProjectSorting />;
  }

  return (
    <SidebarProjectItem {...sharedProps} isManualProjectSorting={false} dragHandleProps={null} />
  );
}

function LooseThreads(props: {
  readonly threads: readonly SidebarThreadSummary[];
  readonly renderThreadList: ProjectGroupedSidebarProps["renderLooseThreadList"];
}) {
  if (props.threads.length === 0) return null;
  return (
    <SidebarMenuItem>
      <div className="px-2 pt-2 text-[10px] font-medium tracking-[0.06em] text-muted-foreground/45 uppercase">
        Loose threads
      </div>
      {props.renderThreadList("__unscoped__", props.threads)}
    </SidebarMenuItem>
  );
}

function SidebarGroupSectionRows({
  section,
  expandedThreadListsByProject,
  canDragProjects,
  projectItemProps,
}: {
  readonly section: SidebarGroupSection;
  readonly expandedThreadListsByProject: ReadonlySet<string>;
  readonly canDragProjects: boolean;
  readonly projectItemProps: ProjectGroupedSidebarProps["projectItemProps"];
}) {
  return (
    <>
      {section.label === null ? null : (
        <SidebarMenuItem>
          <div className="px-2 pt-2 text-[10px] font-medium tracking-[0.06em] text-muted-foreground/45 uppercase">
            {section.label}
          </div>
        </SidebarMenuItem>
      )}
      {section.groups.map((group) => (
        <ProjectGroupRow
          key={group.groupKey}
          group={group}
          isThreadListExpanded={expandedThreadListsByProject.has(group.groupKey)}
          isManualProjectSorting={canDragProjects}
          projectItemProps={projectItemProps}
        />
      ))}
    </>
  );
}

export const ProjectGroupedSidebar = memo(function ProjectGroupedSidebar({
  navigationChrome,
  groups,
  projectExpandedById,
  setProjectExpanded,
  handleStartAddProject,
  density,
  projectSortOrder,
  threadSortOrder,
  onDensityChange,
  onProjectSortOrderChange,
  onThreadSortOrderChange,
  expandedThreadListsByProject,
  isManualProjectSorting,
  attachProjectListAutoAnimateRef,
  projectDnDSensors,
  projectCollisionDetection,
  onProjectDragStart,
  onProjectDragEnd,
  onProjectDragCancel,
  projectImportJobs,
  dismissProjectImportJob,
  runtimeControls,
  cloudStatus,
  emptyState,
  projectItemProps,
  looseThreads,
  renderLooseThreadList,
}: ProjectGroupedSidebarProps) {
  const filters = useSidebarViewFiltersStore((state) => state.filters);
  const setFilters = useSidebarViewFiltersStore((state) => state.setFilters);

  // Status / Environment / Show-empty narrow the list; Group by only decides how
  // the survivors are sectioned, so every count below is taken after filtering.
  const visibleGroups = applySidebarViewFilters(groups, filters);
  const sections = buildSidebarGroupSections(visibleGroups, filters.groupBy);

  const allCollapsed =
    visibleGroups.length > 0 &&
    visibleGroups.every((group) => (projectExpandedById[group.groupKey] ?? true) === false);
  const handleToggleAllCollapsed = () => {
    const expandNext = allCollapsed;
    for (const group of visibleGroups) {
      setProjectExpanded(group.groupKey, expandNext);
    }
  };
  const sortableGroupKeys = visibleGroups.map((group) => group.groupKey);
  // A drop position has no single global order once the list is sectioned, so
  // manual drag is only offered on a flat list.
  const canDragProjects = isManualProjectSorting && isManualOrderingAvailable(filters.groupBy);
  const isImportingProject = projectImportJobs.some((job) => job.status === "cloning");
  const visibleEmptyState =
    visibleGroups.length === 0 &&
    projectImportJobs.length === 0 &&
    looseThreads.length === 0 &&
    cloudStatus === null
      ? emptyState
      : null;

  return (
    <SidebarGroup className="px-2 pt-2 pb-2" aria-busy={isImportingProject ? true : undefined}>
      <SidebarFixedHeader>
        <div className="flex flex-col gap-1 px-2 pt-2">
          <ProjectsHeader
            allCollapsed={allCollapsed}
            hasProjects={visibleGroups.length > 0}
            onAddProject={handleStartAddProject}
            onToggleAllCollapsed={handleToggleAllCollapsed}
            viewOptions={
              <ProjectSidebarViewOptionsMenu
                density={density}
                projectSortOrder={projectSortOrder}
                threadSortOrder={threadSortOrder}
                filters={filters}
                onDensityChange={onDensityChange}
                onProjectSortOrderChange={onProjectSortOrderChange}
                onThreadSortOrderChange={onThreadSortOrderChange}
                onFiltersChange={setFilters}
              />
            }
          />
          <div data-testid="project-navigation-chrome">{navigationChrome}</div>
        </div>
      </SidebarFixedHeader>
      <DndContext
        sensors={projectDnDSensors}
        collisionDetection={projectCollisionDetection}
        onDragStart={onProjectDragStart}
        onDragEnd={onProjectDragEnd}
        onDragCancel={onProjectDragCancel}
      >
        <SortableContext items={sortableGroupKeys} strategy={verticalListSortingStrategy}>
          <SidebarMenu ref={attachProjectListAutoAnimateRef}>
            <ProjectGroupedSidebarEmptyState model={cloudStatus} />
            {projectImportJobs.map((job) => (
              <ProjectImportJobRow
                key={job.id}
                job={job}
                onDismiss={() => dismissProjectImportJob(job.id)}
              />
            ))}
            {sections.map((section) => (
              <SidebarGroupSectionRows
                key={section.key}
                section={section}
                expandedThreadListsByProject={expandedThreadListsByProject}
                canDragProjects={canDragProjects}
                projectItemProps={projectItemProps}
              />
            ))}
            <LooseThreads threads={looseThreads} renderThreadList={renderLooseThreadList} />
            <ProjectGroupedSidebarEmptyState model={visibleEmptyState} />
          </SidebarMenu>
        </SortableContext>
      </DndContext>
      <ProjectRuntimeControls controls={runtimeControls} />
    </SidebarGroup>
  );
});
